import {Observable} from 'rxjs';
import {environment} from '../environments/environment';
import {HttpClient} from '@angular/common/http';
import {GlobalsService} from './services/globals.service';
import {inject} from '@angular/core';
import {SDKQueryPaginatorConfig} from '../../sdk/src/services/query-paginator.service';
import {QueryParams, QueryUtils} from '../../sdk/src/utils/query.utils';

export class CpmCApiBridge {
    private static http: HttpClient;
    private static globalServices: GlobalsService;

    // ===============================================================
    // ===============================================================

    /**
     * Inject required services, called from app.component constructor
     */
    public static _inject() {
        CpmCApiBridge.http = inject(HttpClient);
        CpmCApiBridge.globalServices = inject(GlobalsService);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Get OO8-CPM Backend URL
     * @returns OO8-CPM Backend URL
     */
    public static getBackendBaseURL(): string {
        return environment.backendBaseUrl;
    }
    /**
     * Get current customer UUID if any
     * @returns Customer UUID or undefined
     */
    public static getCurrentCustomerUUID(): string | undefined {
        return this.globalServices.sessionService.getCustomer()?.uuid;
    }
    /**
     * Get current organization UUID if any
     * @returns Organization UUID or undefined
     */
    public static getCurrentOrganizationUUID(): string | undefined {
        return this.globalServices.sessionService.getOrganization()?.uuid;
    }
    /**
     * Get current service request UUID if any
     * @returns Service request UUID or undefined
     */
    public static getCurrentServiceRequestUUID(): string | undefined {
        return this.globalServices.sessionService.getServiceRequest()?.uuid;
    }
    /**
     * Get current task UUID if any
     * @returns Task UUID or undefined
     */
    public static getCurrentTaskUUID(): string | undefined {
        return this.globalServices.sessionService.getTask()?.uuid;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Do a HTTP Get query
     * @param url Query URL
     * @param params Query parameters
     * @returns Results in form of t_Record
     */
    public static httpGet<t_Record>(url: string, params?: QueryParams): Observable<t_Record> {
        return CpmCApiBridge.http.get<t_Record>(url, {
            params: QueryUtils.transformParams(params)
        });
    }
    /**
     * Do a HTTP Post query
     * @param url Query URL
     * @param params Query parameters
     * @returns Results in form of t_Record
     */
    public static httpPost<t_Record>(
        url: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: any,
        params?: QueryParams
    ): Observable<t_Record> {
        return CpmCApiBridge.http.post<t_Record>(url, body, {
            params: QueryUtils.transformParams(params)
        });
    }
    /**
     * Do a HTTP Put query
     * @param url Query URL
     * @param params Query parameters
     * @returns Results in form of t_Record
     */
    public static httpPut<t_Record>(
        url: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: any,
        params?: QueryParams
    ): Observable<t_Record> {
        return CpmCApiBridge.http.put<t_Record>(url, body, {
            params: QueryUtils.transformParams(params)
        });
    }
    /**
     * Do a HTTP Patch query
     * @param url Query URL
     * @param body Request body
     * @param params Query parameters
     * @returns Results in form of t_Record
     */
    public static httpPatch<t_Record>(
        url: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: any,
        params?: QueryParams
    ): Observable<t_Record> {
        return CpmCApiBridge.http.patch<t_Record>(url, body, {
            params: QueryUtils.transformParams(params)
        });
    }
    /**
     * Do a HTTP Delete query
     * @param url Query URL
     * @param params Query parameters
     * @returns Results in form of t_Record
     */
    public static httpDelete<t_Record>(
        url: string,
        params?: QueryParams,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body?: any
    ): Observable<t_Record> {
        return CpmCApiBridge.http.delete<t_Record>(url, {
            params: QueryUtils.transformParams(params),
            body: body
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * SDKQueryPaginatorConfig to CPMQueryPaginatorConfig query params
     * @param sdkQueryPaginatorConfig Input SDKQueryPaginatorConfig
     * @returns Query params in shape of CPMQueryPaginatorConfig
     */
    private static sdkQueryPaginatorConfigTo008CPMQueryPaginatorConfigParams(
        sdkQueryPaginatorConfig: SDKQueryPaginatorConfig
    ): Record<string, string | number | boolean> {
        const res: Record<string, string | number | boolean> = {};

        if (sdkQueryPaginatorConfig?.offset !== undefined) res['_of'] = sdkQueryPaginatorConfig.offset;
        if (sdkQueryPaginatorConfig?.limit !== undefined) res['_l'] = sdkQueryPaginatorConfig.limit;
        if (sdkQueryPaginatorConfig?.sort !== undefined) res['_s'] = sdkQueryPaginatorConfig.sort;
        if (sdkQueryPaginatorConfig?.order !== undefined) res['_or'] = sdkQueryPaginatorConfig.order;
        if (sdkQueryPaginatorConfig?.filter !== undefined) res['_f'] = sdkQueryPaginatorConfig.filter;
        if (sdkQueryPaginatorConfig?.cols_to_search && sdkQueryPaginatorConfig?.cols_to_search.length > 0) {
            const cts = sdkQueryPaginatorConfig.cols_to_search.filter((x) => x).join(',') || '';
            if (cts.length > 0) res['_cts'] = cts;
        }
        if (sdkQueryPaginatorConfig?.filters_rows) {
            const fr = Object.entries(sdkQueryPaginatorConfig.filters_rows)
                .filter((x) => x[1] !== null && x[1] !== undefined)
                .map((x) => `${x[0]}:${x[1]}`)
                .join(',');
            if (fr.length > 0) res['_fr'] = fr;
        }

        return res;
    }
}
